/**
 * Concept Graph Visualization Component
 *
 * D3-based hierarchical graph showing progressive concept relationships.
 * Horizontal layout: Prerequisites (left) → Advanced concepts (right)
 *
 * @module concept-graph/components
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import type { Concept } from '../types';
import {
  buildGraphData,
  getCategoryColor,
  getNodeRadius,
  formatConceptName,
  findLearningPath,
  getLocalNeighborhood,
  type GraphNode,
  type GraphEdge
} from '../utils/graph-layout';

export interface ConceptGraphVisualizationProps {
  concepts: Concept[];
  onConceptClick: (conceptId: string) => void;
  selectedConceptId?: string | null;
  pathMode?: boolean;
  localMode?: boolean; // Show only 1-hop neighborhood around selected concept
  className?: string;
}

/**
 * D3-based concept graph visualization
 */
export function ConceptGraphVisualization({
  concepts,
  onConceptClick,
  selectedConceptId,
  pathMode = false,
  localMode = false,
  className = ''
}: ConceptGraphVisualizationProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<any, any> | null>(null);
  const onConceptClickRef = useRef(onConceptClick);
  const [pathStart, setPathStart] = useState<string | null>(null);
  const [pathEnd, setPathEnd] = useState<string | null>(null);
  const [highlightedPath, setHighlightedPath] = useState<string[]>([]);
  const [legendVisible, setLegendVisible] = useState(true);
  const [breadcrumbTrail, setBreadcrumbTrail] = useState<string[]>([]); // Navigation history
  const legendTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Keep callback ref up to date without triggering re-renders
  useEffect(() => {
    onConceptClickRef.current = onConceptClick;
  }, [onConceptClick]);

  // Update breadcrumb trail when navigating in local mode
  useEffect(() => {
    if (localMode && selectedConceptId) {
      setBreadcrumbTrail(prev => {
        // If this concept is already in the trail, truncate to it (going back)
        const existingIndex = prev.indexOf(selectedConceptId);
        if (existingIndex >= 0) {
          return prev.slice(0, existingIndex + 1);
        }
        // Otherwise, add it to the trail
        return [...prev, selectedConceptId];
      });
    } else if (!localMode) {
      // Clear breadcrumb trail when exiting local mode
      setBreadcrumbTrail([]);
    }
  }, [localMode, selectedConceptId]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || concepts.length === 0) return;

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    // Get container dimensions
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Build graph data
    let graphData = buildGraphData(concepts);

    // If local mode is enabled and a concept is selected, show only 1-hop neighborhood
    if (localMode && selectedConceptId) {
      graphData = getLocalNeighborhood(graphData, selectedConceptId);
    }

    const { nodes, edges: allEdges } = graphData;

    // Filter edges to only include valid ones (where both source and target nodes exist)
    const nodeIds = new Set(nodes.map(n => n.id));
    const edges = allEdges.filter(edge =>
      nodeIds.has(edge.source) && nodeIds.has(edge.target)
    );

    // Create SVG with zoom behavior
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom as any);

    // Calculate bounds for centering
    const xExtent = d3.extent(nodes, d => d.x) as [number, number];
    const yExtent = d3.extent(nodes, d => d.y) as [number, number];
    const graphWidth = xExtent[1] - xExtent[0] + 200;
    const graphHeight = yExtent[1] - yExtent[0] + 200;

    // Center the graph initially
    const initialScale = Math.min(width / graphWidth, height / graphHeight, 1);
    const initialTransform = d3.zoomIdentity
      .translate(width / 2 - (xExtent[0] + xExtent[1]) / 2 * initialScale,
                 height / 2 - (yExtent[0] + yExtent[1]) / 2 * initialScale)
      .scale(initialScale);

    svg.call(zoom.transform as any, initialTransform);

    // Draw swim lane backgrounds for each layer
    const swimLanes = [
      { layer: 'fundamental', y: 0, height: 400, color: '#f0fdf4', label: 'FUNDAMENTALS' },
      { layer: 'language_feature', y: 400, height: 400, color: '#fef3c7', label: 'LANGUAGE FEATURES' },
      { layer: 'pattern', y: 800, height: 400, color: '#fce7f3', label: 'DESIGN PATTERNS' },
      { layer: 'paradigm', y: 1200, height: 400, color: '#e0e7ff', label: 'PARADIGMS' }
    ];

    const swimLaneGroup = g.append('g').attr('class', 'swim-lanes');

    swimLanes.forEach(lane => {
      // Background rectangle
      swimLaneGroup.append('rect')
        .attr('x', xExtent[0] - 100)
        .attr('y', lane.y)
        .attr('width', graphWidth + 200)
        .attr('height', lane.height)
        .attr('fill', lane.color)
        .attr('opacity', 0.15)
        .attr('rx', 8);

      // Lane label (left side)
      swimLaneGroup.append('text')
        .attr('x', xExtent[0] - 80)
        .attr('y', lane.y + 30)
        .attr('fill', '#64748b')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('opacity', 0.6)
        .text(lane.label);
    });

    // Create arrow markers for edges
    svg.append('defs').selectAll('marker')
      .data(['requires', 'enables', 'relates_to'])
      .join('marker')
      .attr('id', d => `arrow-${d}`)
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', d => d === 'requires' ? '#64748B' : d === 'enables' ? '#10B981' : '#94A3B8');

    // Draw edges (will be updated by force simulation)
    const edgeGroup = g.append('g').attr('class', 'edges');

    const edgeElements = edgeGroup.selectAll('line')
      .data(edges)
      .join('line')
        .attr('class', d => `edge edge-${d.type}`)
        .attr('data-source', d => d.source)
        .attr('data-target', d => d.target)
        .attr('stroke', d => d.type === 'requires' ? '#CBD5E1' : d.type === 'enables' ? '#10B981' : '#E2E8F0')
        .attr('stroke-width', d => d.strength * 2)
        .attr('stroke-dasharray', d => d.type === 'relates_to' ? '5,5' : d.type === 'requires' ? '0' : '3,3')
        .attr('marker-end', d => `url(#arrow-${d.type})`)
        .attr('opacity', 0.6);

    // Draw nodes (will be updated by force simulation)
    const nodeGroup = g.append('g').attr('class', 'nodes');

    const nodeElements = nodeGroup.selectAll('.node')
      .data(nodes)
      .join('g')
      .attr('class', 'node')
      .attr('cursor', 'pointer')
      .on('click', (_event, d) => {
        if (pathMode) {
          // Path finding mode
          if (!pathStart) {
            setPathStart(d.id);
          } else if (pathStart === d.id) {
            // Clicked same node, deselect
            setPathStart(null);
            setPathEnd(null);
            setHighlightedPath([]);
          } else {
            // Second node clicked, find path
            setPathEnd(d.id);
            const path = findLearningPath(concepts, pathStart, d.id);
            if (path) {
              setHighlightedPath(path);
            }
          }
        } else {
          // Normal mode: show details (use ref to avoid re-renders)
          onConceptClickRef.current(d.id);
        }
      });

    // Add circles for nodes
    nodeElements.append('circle')
      .attr('r', d => getNodeRadius(d.concept.difficulty))
      .attr('fill', d => getCategoryColor(d.concept.category))
      .attr('stroke', '#FFFFFF')
      .attr('stroke-width', 2)
      .attr('opacity', 0.9)
      .attr('class', d => `node-circle node-${d.id}`);

    // Add difficulty stars
    nodeElements.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 5)
      .attr('fill', '#FFFFFF')
      .attr('font-size', 14)
      .attr('font-weight', 'bold')
      .text(d => '★'.repeat(d.concept.difficulty));

    // Add labels below nodes
    nodeElements.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', d => getNodeRadius(d.concept.difficulty) + 18)
      .attr('fill', '#1E293B')
      .attr('font-size', 12)
      .attr('font-weight', 500)
      .text(d => formatConceptName(d.concept.name, 15));

    // Add "YOU ARE HERE" indicator for selected node in local mode
    if (localMode && selectedConceptId) {
      nodeElements.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', d => getNodeRadius(d.concept.difficulty) + 32)
        .attr('fill', '#9333EA') // Purple-600
        .attr('font-size', 10)
        .attr('font-weight', 'bold')
        .attr('opacity', d => d.id === selectedConceptId ? 1 : 0)
        .text('⭐ YOU ARE HERE');
    }

    // Add tooltips
    nodeElements.append('title')
      .text(d => `${d.concept.name}\nCategory: ${d.concept.category.replace(/_/g, ' ')}\nDifficulty: ${d.concept.difficulty}/5\n\n${d.concept.description}`);

    // Stop previous simulation if it exists
    if (simulationRef.current) {
      simulationRef.current.stop();
    }

    // Create force simulation for automatic spacing and store in ref
    const simulation = d3.forceSimulation(nodes as any)
      // Pull nodes toward their target Y (layer constraint) - STRONG force
      .force('y', d3.forceY<GraphNode>(d => d.targetY).strength(0.5))
      // Gentle horizontal positioning based on depth
      .force('x', d3.forceX<GraphNode>(d => d.depth * 200 + 200).strength(0.1))
      // Prevent overlaps - radius based on node size + padding
      .force('collide', d3.forceCollide<GraphNode>(d => getNodeRadius(d.concept.difficulty) + 25))
      // Link force for edges
      .force('link', d3.forceLink<GraphNode, GraphEdge>(edges as any)
        .id((d: any) => d.id)
        .distance(150)
        .strength(0.2)
      )
      // General repulsion between nodes
      .force('charge', d3.forceManyBody<GraphNode>().strength(-400))
      .alphaDecay(0.015) // Slower cooling for better layout
      .on('tick', () => {
        // Update edge positions
        edgeElements
          .attr('x1', (d: any) => (d.source as GraphNode).x)
          .attr('y1', (d: any) => (d.source as GraphNode).y)
          .attr('x2', (d: any) => (d.target as GraphNode).x)
          .attr('y2', (d: any) => (d.target as GraphNode).y);

        // Update node positions
        nodeElements
          .attr('transform', (d: any) => `translate(${d.x}, ${d.y})`);
      });

    // Store simulation in ref for persistence
    simulationRef.current = simulation;

    // Add drag behavior for interactive adjustment
    const drag = d3.drag<SVGGElement, GraphNode>()
      .on('start', (event) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      })
      .on('drag', (event) => {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      })
      .on('end', (event) => {
        if (!event.active) simulation.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
      });

    nodeElements.call(drag as any);

    // Stop simulation after it settles
    setTimeout(() => {
      simulation.stop();
    }, 10000); // 10 seconds should be enough

    // Highlight path if exists
    if (highlightedPath.length > 0) {
      // Dim all edges
      edgeGroup.selectAll('.edge').attr('opacity', 0.1);

      // Highlight path edges
      for (let i = 0; i < highlightedPath.length - 1; i++) {
        edgeGroup.selectAll('.edge')
          .filter(function() {
            const edge = d3.select(this);
            const source = edge.attr('data-source');
            const target = edge.attr('data-target');
            return (source === highlightedPath[i] && target === highlightedPath[i + 1]) ||
                   (source === highlightedPath[i + 1] && target === highlightedPath[i]);
          })
          .attr('opacity', 1)
          .attr('stroke', '#F97316')
          .attr('stroke-width', 4);
      }

      // Highlight path nodes
      nodeElements.selectAll('circle')
        .attr('opacity', d => highlightedPath.includes(d.id) ? 1 : 0.2)
        .attr('stroke', d => highlightedPath.includes(d.id) ? '#F97316' : '#FFFFFF')
        .attr('stroke-width', d => highlightedPath.includes(d.id) ? 4 : 2);

      nodeElements.selectAll('text')
        .attr('opacity', d => highlightedPath.includes(d.id) ? 1 : 0.3);
    }

    // Cleanup simulation on unmount
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, [concepts, highlightedPath, pathMode, localMode, selectedConceptId]); // Include localMode and selectedConceptId for local graph filtering

  // Separate effect for updating selected node highlight without re-rendering
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);

    // Update all node strokes based on selection
    svg.selectAll<SVGCircleElement, GraphNode>('.node-circle')
      .attr('stroke', d => d.id === selectedConceptId ? '#F97316' : '#FFFFFF')
      .attr('stroke-width', d => d.id === selectedConceptId ? 4 : 2);
  }, [selectedConceptId]);

  // Clear path when path mode is toggled off
  useEffect(() => {
    if (!pathMode) {
      setPathStart(null);
      setPathEnd(null);
      setHighlightedPath([]);
    }
  }, [pathMode]);

  // Auto-hide legend after 3 seconds (invisible UI pattern)
  useEffect(() => {
    // Show legend initially
    setLegendVisible(true);

    // Clear any existing timer
    if (legendTimerRef.current) {
      clearTimeout(legendTimerRef.current);
    }

    // Hide legend after 3 seconds
    legendTimerRef.current = setTimeout(() => {
      setLegendVisible(false);
    }, 3000);

    // Cleanup on unmount
    return () => {
      if (legendTimerRef.current) {
        clearTimeout(legendTimerRef.current);
      }
    };
  }, [concepts]);

  // Show legend on mouse movement (invisible UI pattern)
  const handleMouseMove = () => {
    setLegendVisible(true);

    // Reset auto-hide timer
    if (legendTimerRef.current) {
      clearTimeout(legendTimerRef.current);
    }

    legendTimerRef.current = setTimeout(() => {
      setLegendVisible(false);
    }, 3000);
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full bg-slate-50 rounded-lg ${className}`}
      onMouseMove={handleMouseMove}
    >
      <svg
        ref={svgRef}
        className="w-full h-full"
        data-testid="concept-graph-svg"
      />

      {/* Breadcrumb Trail - Local Mode Navigation */}
      {localMode && breadcrumbTrail.length > 0 && (
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md border border-purple-300 rounded-lg shadow-lg p-3 max-w-2xl">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 flex-1 overflow-x-auto">
              <span className="text-xs font-medium text-purple-700 whitespace-nowrap">Path:</span>
              <div className="flex items-center gap-1.5">
                {breadcrumbTrail.map((conceptId, idx) => {
                  const concept = concepts.find(c => c.id === conceptId);
                  const isLast = idx === breadcrumbTrail.length - 1;
                  return (
                    <React.Fragment key={conceptId}>
                      <button
                        onClick={() => onConceptClickRef.current(conceptId)}
                        className={`text-xs px-2 py-1 rounded transition-colors ${
                          isLast
                            ? 'bg-purple-500 text-white font-semibold'
                            : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                        }`}
                      >
                        {concept?.name || conceptId}
                      </button>
                      {!isLast && (
                        <span className="text-purple-400 text-xs">→</span>
                      )}
                    </React.Fragment>
                  );
                })}
                {breadcrumbTrail.length === 1 && (
                  <span className="text-xs font-semibold text-purple-600 ml-2">
                    ⭐ YOU ARE HERE
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => setBreadcrumbTrail([])}
              className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors whitespace-nowrap"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Path mode indicator */}
      {pathMode && (
        <div className="absolute top-4 left-4 bg-white border border-orange-300 rounded-lg shadow-lg p-4 max-w-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold text-slate-900">Path Finding Mode</span>
          </div>
          {!pathStart && (
            <p className="text-xs text-slate-600">Click a starting concept</p>
          )}
          {pathStart && !pathEnd && (
            <p className="text-xs text-slate-600">
              From: <span className="font-medium text-orange-600">{concepts.find(c => c.id === pathStart)?.name}</span><br />
              Now click the target concept
            </p>
          )}
          {pathStart && pathEnd && highlightedPath.length > 0 && (
            <div className="text-xs text-slate-600">
              <p className="font-medium text-green-600 mb-1">Path found! ({highlightedPath.length} steps)</p>
              <div className="flex flex-wrap gap-1">
                {highlightedPath.map((id, idx) => (
                  <React.Fragment key={id}>
                    <span className="text-orange-600 font-medium">
                      {concepts.find(c => c.id === id)?.name}
                    </span>
                    {idx < highlightedPath.length - 1 && (
                      <span className="text-slate-400">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          {pathStart && pathEnd && highlightedPath.length === 0 && (
            <p className="text-xs text-red-600">No path found between these concepts</p>
          )}
        </div>
      )}

      {/* Horizontal Bottom Legend - Auto-fade invisible UI */}
      <div
        className={`absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md border border-slate-200 rounded-full shadow-lg px-6 py-2 transition-opacity duration-300 ${
          legendVisible ? 'opacity-100' : 'opacity-20'
        }`}
        onMouseEnter={() => setLegendVisible(true)}
        data-testid="legend-horizontal"
      >
        <div className="flex items-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-8 h-0.5 bg-slate-300"></div>
            <span className="text-slate-600">Requires</span>
          </div>
          <div className="w-px h-4 bg-slate-300"></div>
          <div className="flex items-center gap-2">
            <svg className="w-8 h-2" viewBox="0 0 32 2">
              <line x1="0" y1="1" x2="32" y2="1" stroke="#10B981" strokeWidth="2" strokeDasharray="3,3" />
            </svg>
            <span className="text-slate-600">Enables</span>
          </div>
          <div className="w-px h-4 bg-slate-300"></div>
          <div className="flex items-center gap-2">
            <svg className="w-8 h-2" viewBox="0 0 32 2">
              <line x1="0" y1="1" x2="32" y2="1" stroke="#CBD5E1" strokeWidth="2" strokeDasharray="5,5" />
            </svg>
            <span className="text-slate-600">Related</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConceptGraphVisualization;
